/**
 * Authentication Provider for Portfolio Co-Pilot
 * 
 * Handles Cognito OAuth flow and token management.
 * Provides auth state to the entire app via React Context.
 */

import { createContext, useState, useEffect, useCallback } from 'react';
import {
  isCognitoConfigured,
  getLoginUrl,
  getLogoutUrl,
  exchangeCodeForTokens,
  refreshTokens,
  parseJwt,
} from './CognitoConfig';

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'portfolio_access_token',
  ID_TOKEN: 'portfolio_id_token',
  REFRESH_TOKEN: 'portfolio_refresh_token',
  TOKEN_EXPIRY: 'portfolio_token_expiry',
};

// Create context
export const AuthContext = createContext(null);

/**
 * Check if we're in development mode (auth disabled)
 */
function isDevMode() {
  // Check if Cognito is not configured or we're on localhost without prod flag
  return !isCognitoConfigured() || 
    (window.location.hostname === 'localhost' && 
     import.meta.env.VITE_ENVIRONMENT !== 'prod');
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Clear all auth state and storage
   */
  const clearAuth = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }, []);

  /**
   * Save tokens to storage and state
   */
  const saveTokens = useCallback((tokens) => {
    const { access_token, id_token, refresh_token, expires_in } = tokens;
    
    // Calculate expiry time
    const expiryTime = Date.now() + (expires_in * 1000);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
    if (id_token) localStorage.setItem(STORAGE_KEYS.ID_TOKEN, id_token);
    if (refresh_token) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
    
    // Update state
    setAccessToken(access_token);
    setIsAuthenticated(true);
    
    // Parse user info from ID token
    if (id_token) {
      const payload = parseJwt(id_token);
      if (payload) {
        setUser({
          email: payload.email,
          username: payload['cognito:username'] || payload.sub,
          sub: payload.sub,
        });
      }
    }
  }, []);

  /**
   * Check if tokens are expired and refresh if needed
   */
  const checkAndRefreshTokens = useCallback(async () => {
    const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    
    if (!expiry) return false;
    
    const expiryTime = parseInt(expiry, 10);
    const now = Date.now();
    
    // If token expires in less than 5 minutes, refresh it
    if (expiryTime - now < 5 * 60 * 1000 && refreshToken) {
      try {
        const tokens = await refreshTokens(refreshToken);
        saveTokens({ ...tokens, refresh_token: refreshToken });
        return true;
      } catch (e) {
        console.error('Failed to refresh tokens:', e);
        clearAuth();
        return false;
      }
    }
    
    // Token is still valid
    return now < expiryTime;
  }, [saveTokens, clearAuth]);

  /**
   * Handle OAuth callback (exchange code for tokens)
   */
  const handleCallback = useCallback(async (code) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const tokens = await exchangeCodeForTokens(code);
      saveTokens(tokens);
      
      // Clear the code from URL
      window.history.replaceState({}, document.title, '/');
      
      return true;
    } catch (e) {
      console.error('Failed to exchange code for tokens:', e);
      setError(e.message);
      clearAuth();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [saveTokens, clearAuth]);

  /**
   * Initialize auth state from storage
   */
  useEffect(() => {
    async function initAuth() {
      // In dev mode, skip auth
      if (isDevMode()) {
        setIsAuthenticated(true);
        setUser({ email: 'dev@localhost', username: 'developer' });
        setIsLoading(false);
        return;
      }
      
      // Check for OAuth callback code
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        await handleCallback(code);
        return;
      }
      
      // Check for existing tokens
      const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const storedIdToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
      
      if (storedToken) {
        // Verify tokens are still valid
        const isValid = await checkAndRefreshTokens();
        
        if (isValid) {
          setAccessToken(storedToken);
          setIsAuthenticated(true);
          
          // Parse user from ID token
          if (storedIdToken) {
            const payload = parseJwt(storedIdToken);
            if (payload) {
              setUser({
                email: payload.email,
                username: payload['cognito:username'] || payload.sub,
                sub: payload.sub,
              });
            }
          }
        }
      }
      
      setIsLoading(false);
    }
    
    initAuth();
  }, [handleCallback, checkAndRefreshTokens]);

  /**
   * Set up token refresh interval
   */
  useEffect(() => {
    if (!isAuthenticated || isDevMode()) return;
    
    // Check and refresh tokens every 4 minutes
    const interval = setInterval(() => {
      checkAndRefreshTokens();
    }, 4 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, checkAndRefreshTokens]);

  /**
   * Login - redirect to Cognito Hosted UI
   */
  const login = useCallback(() => {
    const loginUrl = getLoginUrl();
    if (loginUrl) {
      window.location.href = loginUrl;
    } else {
      setError('Cognito not configured');
    }
  }, []);

  /**
   * Logout - clear tokens and redirect to Cognito logout
   */
  const logout = useCallback(() => {
    clearAuth();
    
    const logoutUrl = getLogoutUrl();
    window.location.href = logoutUrl;
  }, [clearAuth]);

  /**
   * Get the current access token (for API calls)
   */
  const getAccessToken = useCallback(() => {
    if (isDevMode()) return null;
    return accessToken || localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }, [accessToken]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
    getAccessToken,
    isDevMode: isDevMode(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
